import { trpc } from "../../trpc";
import { formatScriptContentRowRange, shouldUseInlinePersona } from "../../../shared/feishuScriptAssignment";
import type { GlobalAssignmentBlockDiagnostics } from "../../../shared/scriptRoleGlobalAssignmentDiagnostics";
import type { RoleAssignmentItem } from "../../../shared/scriptRoleAssignment";
import type {
  ScriptAssignmentSessionCache,
  ScriptAssignmentTempRole,
  ScriptRoleAssignmentCacheKey,
} from "../../../shared/scriptRoleAssignmentCache";
import type { ScriptContentBeatType } from "../../../shared/scriptRoleDiversity";

export type AssignmentItemStatus =
  | "pending"
  | "inline_persona"
  | "persona"
  | "persona_ready"
  | "matching"
  | "done"
  | "error";

export interface AssignmentProgressItem {
  tempRoleId: string;
  speakerLabel: string;
  utteranceCount: number;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  contentRowNumbers: number[];
  contentRowRange: string;
  status: AssignmentItemStatus;
  error?: string;
  blockDiagnostics?: GlobalAssignmentBlockDiagnostics;
  persona?: import("../../../shared/scriptRoleAssignment").ScriptRolePersona;
  assignment?: RoleAssignmentItem;
}

export function buildAssignmentProgressItems(
  tempRoles: ScriptAssignmentTempRole[],
  cache?: ScriptAssignmentSessionCache | null
): AssignmentProgressItem[] {
  const assignmentMap = new Map(
    (cache?.assignments ?? []).map((item) => [item.tempRoleId, item])
  );

  return tempRoles.map((role) => {
    const persona = cache?.personas[role.tempRoleId];
    const assignment = assignmentMap.get(role.tempRoleId);
    let status: AssignmentItemStatus = "pending";
    if (assignment) {
      status = "done";
    } else if (persona) {
      status = shouldUseInlinePersona(role.utteranceCount) ? "inline_persona" : "persona_ready";
    }

    return {
      tempRoleId: role.tempRoleId,
      speakerLabel: role.speakerLabel,
      utteranceCount: role.utteranceCount,
      contentBeat: role.contentBeat ?? "general",
      firstRowIndex: role.firstRowIndex ?? 0,
      contentRowNumbers: role.contentRowNumbers ?? [],
      contentRowRange: formatScriptContentRowRange(role.contentRowNumbers ?? []),
      status,
      persona,
      assignment,
    };
  });
}

export async function persistAssignmentCache(
  lookup: ScriptRoleAssignmentCacheKey,
  payload: ScriptAssignmentSessionCache
) {
  return trpc.feishuSheets.saveRoleAssignmentCache.mutate({
    ...lookup,
    spreadsheetToken: payload.spreadsheetToken,
    sheetId: payload.sheetId,
    group: lookup.group,
    sourceFingerprint: lookup.sourceFingerprint,
    imageTextsByFileToken: payload.imageTextsByFileToken,
    tempRoles: payload.tempRoles,
    memberCount: payload.memberCount,
    personas: payload.personas,
    assignments: payload.assignments,
    updatedAt: payload.updatedAt ?? new Date().toISOString(),
  });
}

export function areAllPersonasComplete(
  tempRoles: ScriptAssignmentTempRole[],
  personas: Record<string, import("../../../shared/scriptRoleAssignment").ScriptRolePersona>
) {
  return tempRoles.length > 0 && tempRoles.every((role) => Boolean(personas[role.tempRoleId]));
}
